import { BookOpen, Plane, MessageSquare, Clock3, UserRound, Ticket, CreditCard, ShieldCheck, Hotel, Target, History } from 'lucide-react';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { OperatorStatus } from './OperatorStatus';
import { BUNDLED_AIRLINE_CODES, bundledAirlineArtwork, ONEWORLD_ARTWORK, PROGRAMME_TIER_ARTWORK } from './brandArtwork';

const symbols = [
  [Plane, 'Flight / booking'], [MessageSquare, 'Conversation'], [Clock3, 'Waiting / due'],
  [UserRound, 'Owner / customer'], [Ticket, 'Ticket evidence'], [CreditCard, 'Payment evidence'],
  [ShieldCheck, 'Verified check'], [Hotel, 'Hotel / rate'], [Target, 'Target / goal'], [History, 'History'],
] as const;

/** A visible, accessible key for operators; no record data, network or permissions side effects. */
export function OperatorDesignKey() {
  return <Dialog>
    <DialogTrigger asChild>
      <button type="button" className="inline-flex min-h-9 items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100" aria-label="Status colours and symbols">
        <BookOpen className="h-4 w-4" aria-hidden="true" /><span>Status & symbols</span>
      </button>
    </DialogTrigger>
    <DialogContent className="operator-design-guide max-w-3xl">
      <DialogHeader>
        <DialogTitle>Status colours & symbols</DialogTitle>
        <DialogDescription>Read the label and its evidence. Colour supports the state; it does not replace it.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-wrap gap-2">
        <OperatorStatus tone="ready">Ready / successful check</OperatorStatus>
        <OperatorStatus tone="attention">Waiting / needs verification</OperatorStatus>
        <OperatorStatus tone="blocked">Failed / blocked</OperatorStatus>
        <OperatorStatus tone="unknown">Unknown / restricted</OperatorStatus>
        <OperatorStatus tone="info">Action / information</OperatorStatus>
      </div>
      <p className="text-xs leading-relaxed">Green means the named check or state is good to go. A paid invoice, booked flight, issued ticket and delivered message are separate states. Blue identifies actions and information. Brand colours and loyalty-tier colours identify the airline or programme; they are not service-health signals.</p>
      <section aria-label="Operator icons" className="operator-symbols">
        {symbols.map(([Icon, label]) => <div key={label} className="operator-symbol"><Icon className="operator-icon text-[#005eb8]" aria-hidden="true" /><span>{label}</span></div>)}
      </section>
      <h3 className="text-sm font-semibold">Airline artwork</h3>
      <div className="operator-brand-grid">
        {BUNDLED_AIRLINE_CODES.map(code => <div className="operator-brand-example" key={code}><img src={bundledAirlineArtwork(code)} alt={`${code} airline mark`} /><span>{code}</span></div>)}
      </div>
      <h3 className="text-sm font-semibold">oneworld & programme tier artwork</h3>
      <p className="text-xs">Reference examples only. A graphic on a matter must match its recorded carrier, programme, tier or explicitly labelled target.</p>
      <div className="operator-brand-grid">
        {Object.entries(ONEWORLD_ARTWORK).map(([tier, src]) => <div className="operator-brand-example" key={tier}><img src={src} alt={`oneworld ${tier}`} /><span>oneworld {tier}</span></div>)}
        {Object.entries(PROGRAMME_TIER_ARTWORK).flatMap(([carrier, tiers]) => Object.entries(tiers).map(([tier, src]) =>
          <div className="operator-brand-example" key={`${carrier}-${tier}`}><img src={src} alt={`${carrier} ${tier}`} /><span>{carrier === 'BA' ? 'British Airways' : 'Finnair Plus'} · {tier}</span></div>
        ))}
      </div>
    </DialogContent>
  </Dialog>;
}
